import { useState } from 'react';
import { useRedeemPoints } from '../queries';

type RedeemPointsDrawerProps = {
  open: boolean;
  membershipUuid?: string;
  currentBalance?: number;
  redeemPaisePerPoint?: number;
  minRedeem?: number;
  onClose: () => void;
};

const makeId = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const RedeemPointsDrawer = ({
  open,
  membershipUuid,
  currentBalance = 0,
  redeemPaisePerPoint,
  minRedeem = 1,
  onClose
}: RedeemPointsDrawerProps) => {
  const redeemMutation = useRedeemPoints();
  const [points, setPoints] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [error, setError] = useState('');

  if (!open) return null;

  const pointsNum = Math.round(Number(points) || 0);
  const valueRupees = redeemPaisePerPoint && redeemPaisePerPoint > 0 && pointsNum > 0
    ? (pointsNum * redeemPaisePerPoint / 100).toFixed(2)
    : null;

  const handleSubmit = async () => {
    if (!membershipUuid) return;
    if (!Number.isFinite(pointsNum) || pointsNum <= 0) {
      setError('Enter a valid number of points.');
      return;
    }
    if (pointsNum > currentBalance) {
      setError(`Insufficient balance. Available: ${currentBalance} pts.`);
      return;
    }
    if (pointsNum < minRedeem) {
      setError(`Minimum redemption is ${minRedeem} pts.`);
      return;
    }
    setError('');
    try {
      await redeemMutation.mutateAsync({
        membershipUuid,
        points: pointsNum,
        sourceRef: sourceRef.trim() || undefined,
        idempotencyKey: `redeem:${makeId()}`
      });
      setPoints('');
      setSourceRef('');
      onClose();
    } catch {
      setError('Unable to process redemption right now. Please try again.');
    }
  };

  return (
    <div className="membership-drawer-overlay" onClick={onClose}>
      <div className="membership-drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div className="membership-drawer-panel__header">
          <h3>Redeem points</h3>
          <button type="button" className="membership-drawer-panel__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="membership-drawer-panel__form">
          <p className="form-hint">Available balance: <strong>{currentBalance} pts</strong></p>
          <label className="field">
            <span className="label">Points to redeem</span>
            <input
              className="input"
              type="number"
              min={minRedeem}
              max={currentBalance}
              step="1"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder={`Min ${minRedeem} pts`}
            />
          </label>
          {valueRupees !== null ? (
            <p className="form-hint">Value: <strong>₹{valueRupees}</strong></p>
          ) : null}
          <label className="field">
            <span className="label">Source ref <span className="label-optional">(optional)</span></span>
            <input
              className="input"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              placeholder="e.g. Order #456"
            />
          </label>
          {error ? <p className="form-hint danger">{error}</p> : null}
          <div className="membership-drawer-panel__actions">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="btn primary" onClick={handleSubmit} disabled={redeemMutation.isPending}>
              {redeemMutation.isPending ? 'Processing...' : 'Redeem'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedeemPointsDrawer;
